/**
 * The translations of one language, edited inline (a click selects a row, a
 * click on any row saves it once a field changed). Admin only. The MTM twin
 * shows the `DMS_` sections only and forces that prefix.
 */
import {
  useMemo,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PLATFORM, type Platform } from '@/shared/constants';
import { appConfigQueryOptions } from '@/shared/config/app-config';
import { hardNavigate } from '@/shared/lib/navigation';
import { changeLanguage } from '@/shared/i18n/i18n';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { snackbar } from '@/shared/ui/snackbar';
import plusIcon from '@/assets/images/plus.svg';
import {
  translationsQueryOptions,
  updateTranslation,
  type Translation,
} from './api';
import { TranslationDialog } from './TranslationDialog';
import {
  DEFAULT_SORT,
  displayedTranslations,
  fillerRowCount,
  nextSort,
  parseTranslationFilters,
  sectionPrefixOf,
  TRANSLATION_QUERY_PARAMETERS,
  type SortKey,
} from './logic';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'section', label: 'Section' },
  { key: 'key', label: 'Key' },
  { key: 'value', label: 'Value' },
];

const ROW_TEST_IDS: Record<SortKey, string> = {
  section: 'translation-row-section',
  key: 'translation-row-key',
  value: 'translation-row-value',
};

export function TranslationsPage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<{
  platform?: Platform;
}>) {
  const sectionPrefix = sectionPrefixOf(platform);
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const { data: config } = useQuery(appConfigQueryOptions());
  const languages = useMemo(
    () => [...(config?.languages ?? [])].sort((a, b) => a.localeCompare(b)),
    [config?.languages],
  );
  // The language of the interface.
  const [language, setLanguage] = useState(i18n.language ?? '');
  const translations = useQuery({
    ...translationsQueryOptions(language),
    enabled: !!language,
  });
  const [sort, setSort] = useState(DEFAULT_SORT);
  const initialFilters = useMemo(
    () => parseTranslationFilters(window.location.search),
    [],
  );
  const [search, setSearch] = useState(initialFilters.search);
  const [sectionFilter, setSectionFilter] = useState(
    initialFilters.sectionFilter,
  );
  const setSectionFilterFromUi = (next: string) => {
    setSectionFilter(next);
    const url = new URL(window.location.href);
    if (next)
      url.searchParams.set(
        TRANSLATION_QUERY_PARAMETERS.sectionFilter,
        next,
      );
    else
      url.searchParams.delete(TRANSLATION_QUERY_PARAMETERS.sectionFilter);
    window.history.replaceState({}, '', url);
  };
  const [selected, setSelected] = useState<Translation | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const displayed = useMemo(
    () =>
      displayedTranslations(
        translations.data ?? [],
        sort,
        sectionPrefix,
        search,
        sectionFilter,
      ),
    [translations.data, sort, sectionPrefix, search, sectionFilter],
  );

  const sectionOptions = useMemo(() => {
    const sections = new Set(
      (translations.data ?? [])
        .map((item) => item.section ?? '')
        .filter((section) =>
          sectionPrefix
            ? section.startsWith(sectionPrefix)
            : !section.startsWith('DMS_'),
        )
        .filter(Boolean),
    );
    return [...sections].sort((a, b) => a.localeCompare(b));
  }, [translations.data, sectionPrefix]);

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: translationsQueryOptions(language).queryKey,
    });

  const saveTranslation = (item: Translation) => {
    if (sectionPrefix && !(item.section ?? '').startsWith(sectionPrefix)) {
      snackbar(
        `Failed to save translation, section should start with: ${sectionPrefix}`,
      );
      return;
    }
    queryClient.setQueryData(
      translationsQueryOptions(language).queryKey,
      (current: Translation[] | undefined) =>
        current?.map((row) => (row.id === item.id ? item : row)),
    );
    void updateTranslation(item)
      .then(async () => {
        await refresh();
        await changeLanguage(language, i18n);
      })
      .catch(() => undefined); // logged by the API client
  };

  const onRowClicked = (item: Translation) => {
    if (selected?.id === item.id) return;
    setSelected({ ...item });
  };

  const onRowKeyDown = (event: KeyboardEvent, item: Translation) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClicked(item);
    }
  };

  const editField = (key: SortKey, value: string) =>
    setSelected((prev) => (prev ? { ...prev, [key]: value } : prev));

  const onFieldBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!selected) return;
    const original = translations.data?.find((row) => row.id === selected.id);
    const isDirty = original
      ? COLUMNS.some(({ key }) => selected[key] !== original[key])
      : false;
    if (!isDirty) return;
    const row = event.currentTarget.closest('tr');
    const nextTarget = event.relatedTarget;
    // If the row does not change, we do not save yet
    if (row && nextTarget instanceof Node && row.contains(nextTarget)) return;
    saveTranslation(selected);
    setSelected(null);
  };

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialogOpen(false);
    if (result === 'Confirm') void refresh();
  };

  return (
    <div className="container summary">
      <TopMenu platform={platform} />
      <div className="summary-table my-12">
        <div className="language-list mb-12 flex flex-col items-start gap-3">
          <label className="sr-only" htmlFor="translations-language">
            Translations language
          </label>
          <div className="field field-selector">
            <Select
              id="translations-language"
              placeholder="--Select language--"
              options={optionsOf(languages)}
              value={language}
              onChange={(next) => {
                setSelected(null);
                setLanguage(next);
              }}
              testId="translation-language-select"
            />
          </div>
          <div className="flex items-stretch gap-2">
            <label className="sr-only" htmlFor="translations-section">
              Translations section
            </label>
            <div className="field field-selector mt-0 w-[320px]">
              <Select
                id="translations-section"
                placeholder="All sections"
                options={optionsOf(sectionOptions)}
                value={sectionFilter}
                onChange={setSectionFilterFromUi}
                testId="translation-section-select"
              />
            </div>
            <label className="sr-only" htmlFor="translations-search">
              Search translation keys
            </label>
            <input
              id="translations-search"
              className="field mt-0 w-[220px]"
              type="search"
              placeholder="Search translation keys"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <label className="flex items-center gap-2" htmlFor="translations-mtm">
            <input
              id="translations-mtm"
              type="checkbox"
              checked={platform === PLATFORM.DMS}
              onChange={(event) =>
                hardNavigate(
                  event.target.checked ? '/mtm/translations' : '/translations',
                )
              }
            />
            <span>MTM</span>
          </label>
        </div>
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-translation"
          onClick={() => setDialogOpen(true)}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a translation</span>
        </button>
        <div className="table-container">
          <div
            className="items-container"
            role="region"
            aria-label="Translations"
          >
            <table className="table items">
              <thead>
                <tr>
                  {COLUMNS.map(({ key, label }) => (
                    <SortableHeader
                      key={key}
                      label={label}
                      width="33%"
                      active={sort.by === key}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextSort(current, key))
                      }
                      testId={`translation-header-${key}`}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((item) => {
                  const editing = selected?.id === item.id;
                  return (
                    // A row is selected and saved by clicking it; the
                    // keyboard handler is the accessible counterpart.
                    <tr
                      key={item.id}
                      className={`translation-row ${editing ? 'editing' : ''}`}
                      tabIndex={0}
                      aria-selected={editing}
                      onClick={() => onRowClicked(item)}
                      onKeyDown={(event) => onRowKeyDown(event, item)}
                    >
                      {COLUMNS.map(({ key, label }) => (
                        <td
                          key={key}
                          style={{ width: '33%' }}
                          title={item[key] ?? ''}
                          data-testid={ROW_TEST_IDS[key]}
                        >
                          {editing && selected ? (
                            <input
                              className="field"
                              name={key}
                              aria-label={label}
                              data-testid={`${ROW_TEST_IDS[key]}-input`}
                              value={selected[key] ?? ''}
                              onChange={(event) =>
                                editField(key, event.target.value)
                              }
                              onBlur={onFieldBlur}
                            />
                          ) : (
                            <span>{item[key]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {Array.from(
                  { length: fillerRowCount(displayed.length) },
                  (_, index) => (
                    <tr key={`empty-${index}`} className="empty-row">
                      <td style={{ width: '33%' }} />
                      <td style={{ width: '33%' }} />
                      <td style={{ width: '33%' }} />
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {dialogOpen && (
        <TranslationDialog
          languages={languages}
          sectionPrefix={sectionPrefix}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
